import z from 'zod';
import { dateSchema } from '../../validation';
import { businessSchema } from '../../common';
import { leadLostReasonCodeSchema } from './lead.lost-reason.model';

/**
 * What the lead was asked to buy when they said no.
 *
 * One member today, and an enum anyway: the offer that produced this
 * record is the post-وثيقة subscription ask, and the next one (a
 * financing offer, a seat upgrade) has to be distinguishable from it or
 * the field stops answering "no to what?".
 */
export const leadObjectionAskSchema = z.enum(['subscribe']);
export type LeadObjectionAsk = z.infer<typeof leadObjectionAskSchema>;

/**
 * A "no" we asked the reason for, and what they said.
 *
 * Kept apart from {@link leadLostReasonSchema}, which is terminal: a
 * lost reason closes the lead, and someone who declined the subscription
 * after their first free وثيقة is not lost — they are a customer who has
 * used the product and not paid for it yet, which is the most valuable
 * objection the funnel can collect. Marking them `lost` would drop them
 * out of every report that matters.
 *
 * `note` is verbatim and untranslated, because the wording IS the
 * finding — "غالي شوي، عندي 3 وثائق بالشهر" says both the price
 * objection and the volume that caused it, and either paraphrase loses
 * one of them. `code` shares the lost-reason vocabulary so the two can
 * be counted together, and is optional because nothing classifies an
 * objection at capture time: a code nobody fills is a column nobody
 * trusts, so it is left for whoever reads the notes.
 */
export const leadObjectionSchema = z.object({
  at: dateSchema,
  /** Which business's offer was turned down. */
  business: businessSchema,
  ask: leadObjectionAskSchema,
  /** Their answer, in their own words. */
  note: z.string().optional(),
  /** Classification, when someone has made one. */
  code: leadLostReasonCodeSchema.optional(),
});
export type LeadObjection = z.infer<typeof leadObjectionSchema>;
